/**
 * The SyncState is used to keep track of the current value of the SyncGroups.
 *
 * SelectOne and SelectMany uses fully separate states internally since the two types of features
 * are synced separately. See SyncGroupsHandler for details.
 */
export class SyncGroupsState {
    /**
     * @param verboseLogging Set to true to get verbose sync state changes logged to the console.
     */
    constructor(_verboseLogging, initial) {
        this._verboseLogging = _verboseLogging;
        this._selectOne = new Map();
        this._selectMany = new Map();
        this.setCompact(initial !== null && initial !== void 0 ? initial : {});
    }
    get verboseLogging() {
        return this._verboseLogging;
    }
    /**
     * Set to true to get verbose sync state changes logged to the console.
     */
    set verboseLogging(v) {
        this._verboseLogging = v;
    }
    /**
     * @returns a deep copy of the SyncGroupState.
     */
    clone() {
        return new SyncGroupsState(this._verboseLogging, this.getCompact());
    }
    /**
     * Replaces the internal state of this SyncGroupState with a deep copy of the one in source.
     * @returns the updated SyncGroupState.
     */
    copyFrom(source) {
        this._selectOne.clear();
        this._selectMany.clear();
        // The Exclude part below ensures that OptionCode is a defined as a primitive.
        // eslint-disable-next-line @typescript-eslint/ban-types
        let entryOne;
        for (entryOne of source._selectOne) {
            this._selectOne.set(...entryOne);
        }
        let entryMany;
        for (entryMany of source._selectMany) {
            this._selectMany.set(entryMany[0], new Map(entryMany[1]));
        }
        return this;
    }
    setForSelectOne(syncCode, optionCode) {
        this._selectOne.set(syncCode, optionCode);
        this.log(syncCode, optionCode);
    }
    setForSelectMany(syncCode, optionCode, selected) {
        let entry = this._selectMany.get(syncCode);
        if (entry === undefined) {
            entry = new Map();
            this._selectMany.set(syncCode, entry);
        }
        entry.set(optionCode, selected);
        this.log(syncCode, optionCode, selected);
    }
    getForSelectOne(syncCode) {
        return this._selectOne.get(syncCode);
    }
    getForSelectMany(syncCode, optionCode) {
        var _a;
        return (_a = this._selectMany.get(syncCode)) === null || _a === void 0 ? void 0 : _a.get(optionCode);
    }
    /** Overrides the current sync state */
    setCompact(s) {
        this._selectOne.clear();
        this._selectMany.clear();
        const { selectOne, selectMany } = s;
        if (selectOne !== undefined) {
            for (const { syncCode, optionCode } of selectOne) {
                this._selectOne.set(syncCode, optionCode);
            }
        }
        if (selectMany !== undefined) {
            for (const { syncCode, optionCode, selected } of selectMany) {
                let entry = this._selectMany.get(syncCode);
                if (entry === undefined) {
                    entry = new Map();
                    this._selectMany.set(syncCode, entry);
                }
                entry.set(optionCode, selected);
            }
        }
    }
    getCompact() {
        const result = {};
        if (this._selectOne.size !== 0) {
            const resultSelectOne = [];
            for (const [syncCode, optionCode] of this._selectOne) {
                resultSelectOne.push({ syncCode, optionCode });
            }
            result.selectOne = resultSelectOne;
        }
        if (this._selectMany.size !== 0) {
            const resultSelectMany = [];
            for (const [syncCode, optionCodeToSelected] of this._selectMany) {
                for (const [optionCode, selected] of optionCodeToSelected) {
                    resultSelectMany.push({ syncCode, optionCode, selected });
                }
            }
            result.selectMany = resultSelectMany;
        }
        return result;
    }
    log(syncCode, optionCode, selected) {
        if (!this._verboseLogging) {
            return;
        }
        const isMany = selected !== undefined;
        const selectOne = Array.from(this._selectOne.entries());
        const selectMany = Array.from(this._selectMany.entries()).reduce((a, [groupCode, optionCodeToSelected]) => {
            for (const [optionCode, selected] of optionCodeToSelected) {
                a.push([groupCode, optionCode, selected]);
            }
            return a;
        }, []);
        let columnWidth = 1;
        selectOne.forEach((e) => {
            columnWidth = Math.max(columnWidth, e[0].length);
        });
        selectMany.forEach((e) => {
            columnWidth = Math.max(columnWidth, e[0].length);
        });
        const padding = Array(columnWidth).join(" ");
        const bold = "font-weight: bold";
        const styles = [];
        let msg = "";
        if (syncCode !== undefined && isMany) {
            msg += `%c${optionCode !== null && optionCode !== void 0 ? optionCode : ""}%c in %c${syncCode}%c set to %c${selected ? "on" : "off"}`;
            styles.push(bold, "", bold, "", bold);
        }
        else if (syncCode !== undefined && !isMany) {
            msg += `%c${syncCode}%c set to %c${optionCode !== null && optionCode !== void 0 ? optionCode : ""}`;
            styles.push(bold, "", bold);
        }
        msg += "\n\n%cSync State (single)";
        styles.push(bold);
        selectOne.forEach((e) => {
            const match = !isMany && syncCode === e[0];
            msg += `\n%c${match ? "*" : " "} ${(padding + e[0]).slice(-columnWidth)}: ${e[1]}`;
            styles.push(match ? bold : "");
        });
        if (selectOne.length === 0) {
            msg += "\n%c <Empty>";
            styles.push("");
        }
        msg += "\n\n%cSync State (multi)";
        styles.push(bold);
        selectMany.forEach((e) => {
            const match = isMany && syncCode === e[0] && optionCode === e[1];
            msg += `\n%c${match ? "*" : " "} ${(padding + e[0]).slice(-columnWidth)}: ${e[2] === true ? "\u2705" : "\u274c"} ${e[1]}`;
            styles.push(match ? bold : "");
        });
        if (selectMany.length === 0) {
            msg += "\n%c <Empty>";
            styles.push("");
        }
        console.log(msg, ...styles);
    }
}
